import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { IconArrowsDiagonal } from '@tabler/icons-react';
import 'mapbox-gl/dist/mapbox-gl.css';
import './RouteOverviewMinimap.css';
import RouteOverviewGlobeModal from './RouteOverviewGlobeModal';

// Small 2D inset map ("Google Maps overlay" style) showing the full route
// outline and a marker for the current position. Hidden on mobile via CSS.
// Clicking the minimap opens an expanded globe view for planetary context.
//
// Props:
//   coordinates: [lng, lat][] — full route polyline
//   currentPosition: [lng, lat] | null — live position marker (e.g. scrub head)
//   theme: { primary, secondary, ... } — colours for the route + marker
//   switchCheckpointIndex: number | null — index in `coordinates` where the
//     route line splits into primary/secondary halves. Optional.
//   routeTitle: string — passed through to the expanded globe modal header
//   routeDistanceKm: number
//   startLabel: string — start-checkpoint pin label in the globe modal
//   endLabel: string — end-checkpoint pin label
//   onClick: () => void — optional extra click handler (fires alongside expand)
export default function RouteOverviewMinimap({
  coordinates,
  currentPosition,
  theme,
  switchCheckpointIndex,
  routeTitle,
  routeDistanceKm,
  startLabel,
  endLabel,
  onClick,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const loadedRef = useRef(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Init the second Mapbox instance once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    if (!coordinates || coordinates.length < 2) return;

    const bounds = coordinates.reduce(
      (b, c) => b.extend(c),
      new mapboxgl.LngLatBounds(coordinates[0], coordinates[0])
    );

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/light-v11',
      bounds,
      fitBoundsOptions: { padding: 16, animate: false },
      interactive: false,
      attributionControl: false,
      logoPosition: 'bottom-right',
    });
    mapRef.current = map;

    map.on('load', () => {
      loadedRef.current = true;
      const switchIdx =
        Number.isInteger(switchCheckpointIndex) &&
        switchCheckpointIndex > 0 &&
        switchCheckpointIndex < coordinates.length
          ? switchCheckpointIndex
          : null;

      if (switchIdx) {
        const pre = coordinates.slice(0, switchIdx + 1);
        const post = coordinates.slice(switchIdx);
        map.addSource('route-pre', {
          type: 'geojson',
          data: { type: 'Feature', geometry: { type: 'LineString', coordinates: pre } },
        });
        map.addSource('route-post', {
          type: 'geojson',
          data: { type: 'Feature', geometry: { type: 'LineString', coordinates: post } },
        });
        map.addLayer({
          id: 'route-pre',
          type: 'line',
          source: 'route-pre',
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-color': theme.primary, 'line-width': 3 },
        });
        map.addLayer({
          id: 'route-post',
          type: 'line',
          source: 'route-post',
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-color': theme.secondary, 'line-width': 3 },
        });
      } else {
        map.addSource('route', {
          type: 'geojson',
          data: { type: 'Feature', geometry: { type: 'LineString', coordinates } },
        });
        map.addLayer({
          id: 'route',
          type: 'line',
          source: 'route',
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-color': theme.primary, 'line-width': 3 },
        });
      }
    });

    return () => {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      map.remove();
      mapRef.current = null;
      loadedRef.current = false;
    };
  }, [coordinates, theme.primary, theme.secondary, switchCheckpointIndex]);

  // Move / create the current-position marker as it updates.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !currentPosition) {
      if (markerRef.current && !currentPosition) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      return;
    }
    if (!markerRef.current) {
      const el = document.createElement('div');
      el.className = 'route-overview-marker';
      el.style.background = theme.primary;
      el.style.borderColor = '#ffffff';
      markerRef.current = new mapboxgl.Marker({ element: el })
        .setLngLat(currentPosition)
        .addTo(map);
    } else {
      markerRef.current.setLngLat(currentPosition);
    }
  }, [currentPosition, theme.primary]);

  const handleClick = () => {
    setIsExpanded(true);
    if (onClick) onClick();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <>
      <div
        className="route-overview-minimap"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-label="Open route overview"
      >
        <div ref={containerRef} className="route-overview-minimap-canvas" />
        <div className="route-overview-expand-icon" aria-hidden="true">
          <IconArrowsDiagonal size={14} />
        </div>
      </div>
      {isExpanded && (
        <RouteOverviewGlobeModal
          coordinates={coordinates}
          theme={theme}
          switchCheckpointIndex={switchCheckpointIndex}
          routeTitle={routeTitle}
          routeDistanceKm={routeDistanceKm}
          startLabel={startLabel}
          endLabel={endLabel}
          onClose={() => setIsExpanded(false)}
        />
      )}
    </>
  );
}
